import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './dash.css';
import { getFirestore, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { app } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDay,
  faCommentSms,
  faEnvelope,
  faFileInvoice,
  faMoneyBill,
  faRotate,
  faTriangleExclamation,
  faUserGraduate,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import Spinner from 'react-bootstrap/Spinner';

const MAIL_API_BASE_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/mailApi';
const SMS_BALANCE_API_URL = 'https://us-central1-ubunifu-college.cloudfunctions.net/smsBalanceCheck/sms-balance';

const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const dateObj = new Date(value);
  return Number.isNaN(dateObj.getTime()) ? null : dateObj;
};

const isSameDay = (value, start, end) => {
  const dateObj = toDateSafe(value);
  if (!dateObj) return false;
  return dateObj >= start && dateObj <= end;
};

const formatDateTime = (value) => {
  const dateObj = toDateSafe(value);
  if (!dateObj) return 'Not available';
  return `${dateObj.toLocaleDateString()} • ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatCurrency = (value, currency = 'KES') => {
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
  const amount = Number(value || 0);
  return `${safeCurrency} ${amount.toLocaleString(safeCurrency === 'USD' ? 'en-US' : 'en-KE', {
    minimumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
    maximumFractionDigits: safeCurrency === 'USD' ? 2 : 0
  })}`;
};

const getSmsBalanceLevel = (credits) => {
  const numericCredits = Number(credits);
  if (!Number.isFinite(numericCredits)) return 'unknown';
  if (numericCredits < 50) return 'critical';
  if (numericCredits < 200) return 'warning';
  return 'healthy';
};

function DailyBriefingComp() {
  const db = getFirestore(app);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [loadError, setLoadError] = useState('');

  const fetchBriefing = useCallback(async (showRefreshState = false) => {
    if (showRefreshState) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setLoadError('');

    try {
      const now = new Date();
      const startOfDay = new Date(now);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const [
        inactiveUsersSnap,
        activeStudentsSnap,
        interviewsSnap,
        incomeSnap,
        admissionFeesSnap,
        coursesSnap,
        mediaConsentRequestsSnap,
        mediaConsentTokensSnap
      ] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'inactive'))),
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
        getDocs(collection(db, 'interviewCalls')),
        getDocs(query(collection(db, 'Income'), orderBy('paymentDate', 'desc'))),
        getDocs(query(collection(db, 'AdmissionFeePayments'), orderBy('paymentDate', 'desc'))),
        getDocs(collection(db, 'courses')),
        getDocs(query(collection(db, 'MediaConsentRequests'), orderBy('createdAt', 'desc'))),
        getDocs(collection(db, 'MediaConsentTokens'))
      ]);

      const courseMap = {};
      coursesSnap.forEach((courseDoc) => {
        const data = courseDoc.data();
        courseMap[data.mcourseName] = {
          price: parseFloat(data.mcoursePrice || 0),
          installment: parseFloat(data.feeInstallments || 0)
        };
      });

      const applicationsToday = [];
      const pendingApplications = [];
      inactiveUsersSnap.forEach((userDoc) => {
        const data = userDoc.data();
        const record = {
          id: userDoc.id,
          userId: data.userId || userDoc.id,
          name: data.bio || 'Applicant',
          course: data.course || 'Course pending',
          appDate: data.appDate
        };

        pendingApplications.push(record);
        if (isSameDay(data.appDate, startOfDay, endOfDay)) {
          applicationsToday.push(record);
        }
      });

      applicationsToday.sort((first, second) => (
        (toDateSafe(second.appDate)?.getTime() || 0) - (toDateSafe(first.appDate)?.getTime() || 0)
      ));

      const interviewsToday = [];
      const activeInterviews = [];
      interviewsSnap.forEach((interviewDoc) => {
        const data = interviewDoc.data();
        const status = String(data.status || '').toLowerCase();
        const record = {
          id: interviewDoc.id,
          userId: data.userId || interviewDoc.id,
          studentName: data.studentName || 'Applicant',
          course: data.course || 'Course pending',
          scheduledFor: data.scheduledFor,
          status: data.status || 'scheduled',
          meetingId: data.meetingId || data.roomName || ''
        };

        if (status === 'active') {
          activeInterviews.push(record);
        }

        if (isSameDay(data.scheduledFor, startOfDay, endOfDay)) {
          interviewsToday.push(record);
        }
      });

      interviewsToday.sort((first, second) => (
        (toDateSafe(first.scheduledFor)?.getTime() || 0) - (toDateSafe(second.scheduledFor)?.getTime() || 0)
      ));

      const regularPaymentsToday = [];
      const incomeByStudent = {};
      let totalFeePaymentsToday = 0;

      incomeSnap.forEach((incomeDoc) => {
        const data = incomeDoc.data();
        const amount = Number(data.incomeAmount || data.amountInKes || 0);
        const record = {
          id: incomeDoc.id,
          studentId: data.studentId || '',
          studentName: data.studentName || 'Student',
          amount,
          currency: data.displayCurrency || data.paymentCurrency || data.currency || 'KES',
          displayAmount: Number(data.displayAmount || amount),
          paymentDate: data.paymentDate,
          paymentMethod: data.paymentMethod || 'Not provided',
          type: 'Fee payment'
        };

        if (record.studentId) {
          if (!incomeByStudent[record.studentId]) incomeByStudent[record.studentId] = [];
          incomeByStudent[record.studentId].push({
            amount,
            paymentDate: toDateSafe(data.paymentDate)
          });
        }

        if (isSameDay(data.paymentDate, startOfDay, endOfDay)) {
          regularPaymentsToday.push(record);
          totalFeePaymentsToday += amount;
        }
      });

      const admissionPaymentsToday = [];
      let totalAdmissionFeesToday = 0;

      admissionFeesSnap.forEach((paymentDoc) => {
        const data = paymentDoc.data();
        const amount = Number(data.amount || 0);
        const record = {
          id: paymentDoc.id,
          studentId: data.userId || '',
          studentName: data.studentName || 'Applicant',
          amount,
          currency: data.displayCurrency || data.paymentCurrency || data.currency || 'KES',
          displayAmount: Number(data.displayAmount || amount),
          paymentDate: data.paymentDate,
          paymentMethod: data.paymentMethod || 'Not provided',
          type: 'Admission fee'
        };

        if (isSameDay(data.paymentDate, startOfDay, endOfDay)) {
          admissionPaymentsToday.push(record);
          totalAdmissionFeesToday += amount;
        }
      });

      const paymentsToday = [...regularPaymentsToday, ...admissionPaymentsToday].sort((first, second) => (
        (toDateSafe(second.paymentDate)?.getTime() || 0) - (toDateSafe(first.paymentDate)?.getTime() || 0)
      ));

      const overdueStudents = [];
      activeStudentsSnap.forEach((studentDoc) => {
        const data = studentDoc.data();
        const userId = data.userId || studentDoc.id;
        const course = courseMap[data.course];
        const admissionDate = toDateSafe(data.admissionDate || data.appDate);

        if (!course || !admissionDate) return;

        const dueDay = admissionDate.getDate();
        const isPastDueDay = now.getDate() > dueDay;
        if (!isPastDueDay) return;

        const incomes = incomeByStudent[userId] || [];
        const coursePrice = Number(course.price || 0);
        const monthlyInstallment = Number(course.installment || 0);
        if (coursePrice <= 0 || monthlyInstallment <= 0) return;

        let totalPaid = 0;
        let paidThisMonth = 0;
        incomes.forEach((income) => {
          totalPaid += Number(income.amount || 0);
          if (
            income.paymentDate &&
            income.paymentDate.getMonth() === currentMonth &&
            income.paymentDate.getFullYear() === currentYear
          ) {
            paidThisMonth += Number(income.amount || 0);
          }
        });

        const balance = Math.max(coursePrice - totalPaid, 0);
        if (balance <= 0) return;

        const amountDue = Math.min(Math.max(monthlyInstallment - paidThisMonth, 0), balance);
        if (amountDue <= 0) return;

        overdueStudents.push({
          id: studentDoc.id,
          userId,
          name: data.bio || 'Student',
          course: data.course || 'Course',
          amountDue,
          balance
        });
      });

      overdueStudents.sort((first, second) => second.amountDue - first.amountDue);

      const mediaConsentRequests = mediaConsentRequestsSnap.docs.map((requestDoc) => ({
        id: requestDoc.id,
        ...requestDoc.data()
      }));

      const mediaStatsMap = {};
      mediaConsentTokensSnap.forEach((tokenDoc) => {
        const data = tokenDoc.data();
        const requestId = data.requestId;
        if (!requestId) return;

        if (!mediaStatsMap[requestId]) {
          mediaStatsMap[requestId] = { total: 0, approved: 0, declined: 0, pending: 0 };
        }

        mediaStatsMap[requestId].total += 1;
        if (data.status === 'approved') {
          mediaStatsMap[requestId].approved += 1;
        } else if (data.status === 'declined') {
          mediaStatsMap[requestId].declined += 1;
        } else {
          mediaStatsMap[requestId].pending += 1;
        }
      });

      const pendingMediaRequests = mediaConsentRequests
        .map((request) => {
          const stats = mediaStatsMap[request.id] || { total: 0, approved: 0, declined: 0, pending: 0 };
          return {
            ...request,
            ...stats
          };
        })
        .filter((request) => request.pending > 0 || request.total === 0)
        .slice(0, 5);

      let unreadEmails = null;
      let smsCredits = null;
      let smsCreditsLevel = 'unknown';

      try {
        const emailResponse = await fetch(`${MAIL_API_BASE_URL}/read-inbox?limit=50`);
        const emailData = await emailResponse.json();
        if (emailResponse.ok && Array.isArray(emailData.emails)) {
          unreadEmails = emailData.emails.filter((email) => !email.seen).length;
        }
      } catch (error) {
        console.error('Failed to load inbox health for daily briefing:', error);
      }

      try {
        const smsResponse = await fetch(SMS_BALANCE_API_URL);
        const smsData = await smsResponse.json();
        if (smsResponse.ok) {
          smsCredits = Number(smsData?.data?.sms_credits);
          smsCreditsLevel = getSmsBalanceLevel(smsCredits);
        }
      } catch (error) {
        console.error('Failed to load SMS balance for daily briefing:', error);
      }

      setBriefing({
        generatedAt: new Date(),
        applicationsToday,
        pendingApplicationsCount: pendingApplications.length,
        interviewsToday,
        activeInterviewsCount: activeInterviews.length,
        paymentsToday,
        totalFeePaymentsToday,
        totalAdmissionFeesToday,
        overdueStudents,
        pendingMediaRequests,
        pendingMediaResponsesCount: pendingMediaRequests.reduce((sum, request) => sum + Number(request.pending || 0), 0),
        unreadEmails,
        smsCredits: Number.isFinite(smsCredits) ? smsCredits : null,
        smsCreditsLevel
      });
    } catch (error) {
      console.error('Failed to build daily briefing:', error);
      setLoadError('We could not prepare the daily briefing right now.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchBriefing();
  }, [fetchBriefing]);

  const priorities = useMemo(() => {
    if (!briefing) return [];

    const items = [];

    if (briefing.applicationsToday.length > 0) {
      items.push({
        title: `${briefing.applicationsToday.length} new application${briefing.applicationsToday.length === 1 ? '' : 's'} today`,
        detail: 'Review fresh applicants and move the strongest ones toward interview scheduling.',
        actionLabel: 'Open applications',
        action: () => navigate('/applications')
      });
    }

    if (briefing.interviewsToday.length > 0) {
      items.push({
        title: `${briefing.interviewsToday.length} interview${briefing.interviewsToday.length === 1 ? '' : 's'} scheduled today`,
        detail: 'Prepare the team and confirm that invite timing and meeting links are still correct.',
        actionLabel: 'Open interviews',
        action: () => navigate('/interviews')
      });
    }

    if (briefing.overdueStudents.length > 0) {
      items.push({
        title: `${briefing.overdueStudents.length} student${briefing.overdueStudents.length === 1 ? '' : 's'} need fee follow-up`,
        detail: `There are overdue balances that need attention, starting with the highest monthly due amounts.`,
        actionLabel: 'Open fee reminders',
        action: () => navigate('/fee-reminders')
      });
    }

    if (briefing.paymentsToday.length > 0) {
      items.push({
        title: `${briefing.paymentsToday.length} payment${briefing.paymentsToday.length === 1 ? '' : 's'} recorded today`,
        detail: 'Reconcile today’s incoming payments and confirm the related student records are up to date.',
        actionLabel: 'Open income',
        action: () => navigate('/income')
      });
    }

    if (briefing.pendingMediaResponsesCount > 0) {
      items.push({
        title: `${briefing.pendingMediaResponsesCount} media consent response${briefing.pendingMediaResponsesCount === 1 ? '' : 's'} still pending`,
        detail: 'Follow up on outstanding consent requests before using new student content.',
        actionLabel: 'Open media consent',
        action: () => navigate('/media-consent')
      });
    }

    if (briefing.unreadEmails > 0) {
      items.push({
        title: `${briefing.unreadEmails} unread email${briefing.unreadEmails === 1 ? '' : 's'} in the shared inbox`,
        detail: 'Clear inbox backlog early so applicant and student communication does not drift.',
        actionLabel: 'Open emails',
        action: () => navigate('/emails')
      });
    }

    if (briefing.smsCredits !== null && briefing.smsCredits < 200) {
      items.push({
        title: `SMS credits are ${briefing.smsCreditsLevel === 'critical' ? 'critically low' : 'running low'}`,
        detail: `Current balance is ${briefing.smsCredits} credits. Plan a top-up before communications are blocked.`,
        actionLabel: 'Open SMS',
        action: () => navigate('/sms')
      });
    }

    if (items.length === 0) {
      items.push({
        title: 'The platform looks calm this morning',
        detail: 'No major issues are standing out right now. Use the modules below to spot-check operations and keep the day moving.',
        actionLabel: 'Open dashboard',
        action: () => navigate('/dashboard')
      });
    }

    return items.slice(0, 6);
  }, [briefing, navigate]);

  if (loading) {
    return (
      <div className="dailyBriefingPage">
        <div className="dailyBriefingLoadingCard">
          <Spinner animation="border" size="sm" />
          <span>Preparing today&apos;s briefing...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="dailyBriefingPage">
        <div className="dailyBriefingErrorCard">
          <h4>Daily briefing unavailable</h4>
          <p>{loadError}</p>
          <button type="button" className="dailyBriefingActionBtn" onClick={() => fetchBriefing(true)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dailyBriefingPage">
      <div className="dailyBriefingHero">
        <div>
          <p className="dailyBriefingEyebrow">Communications daily briefing</p>
          <h2>What matters today</h2>
          <p>
            A quick operational snapshot of applications, interviews, payments, fee risk,
            communications health, and consent follow-ups from across the platform.
          </p>
        </div>
        <div className="dailyBriefingHeroMeta">
          <div>
            <span>Date</span>
            <strong>{briefing.generatedAt.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
          </div>
          <div>
            <span>Last refreshed</span>
            <strong>{briefing.generatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
          </div>
          <button type="button" className="dailyBriefingRefreshBtn" onClick={() => fetchBriefing(true)} disabled={refreshing}>
            <FontAwesomeIcon icon={faRotate} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh briefing'}</span>
          </button>
        </div>
      </div>

      <div className="dailyBriefingStatsGrid">
        <div className="dailyBriefingStatCard">
          <span className="dailyBriefingStatIcon"><FontAwesomeIcon icon={faUserGraduate} /></span>
          <p>New applications today</p>
          <h3>{briefing.applicationsToday.length}</h3>
          <small>{briefing.pendingApplicationsCount} pending applicants total</small>
        </div>
        <div className="dailyBriefingStatCard">
          <span className="dailyBriefingStatIcon"><FontAwesomeIcon icon={faVideo} /></span>
          <p>Interviews today</p>
          <h3>{briefing.interviewsToday.length}</h3>
          <small>{briefing.activeInterviewsCount} active right now</small>
        </div>
        <div className="dailyBriefingStatCard">
          <span className="dailyBriefingStatIcon"><FontAwesomeIcon icon={faMoneyBill} /></span>
          <p>Payments today</p>
          <h3>{briefing.paymentsToday.length}</h3>
          <small>{formatCurrency(briefing.totalFeePaymentsToday + briefing.totalAdmissionFeesToday, 'KES')} collected today</small>
        </div>
        <div className="dailyBriefingStatCard">
          <span className="dailyBriefingStatIcon"><FontAwesomeIcon icon={faFileInvoice} /></span>
          <p>Fee follow-ups due</p>
          <h3>{briefing.overdueStudents.length}</h3>
          <small>Students past their monthly fee window</small>
        </div>
        <div className="dailyBriefingStatCard">
          <span className="dailyBriefingStatIcon"><FontAwesomeIcon icon={faEnvelope} /></span>
          <p>Unread emails</p>
          <h3>{briefing.unreadEmails ?? '—'}</h3>
          <small>Shared inbox health</small>
        </div>
        <div className="dailyBriefingStatCard">
          <span className="dailyBriefingStatIcon"><FontAwesomeIcon icon={faCommentSms} /></span>
          <p>SMS credits</p>
          <h3>{briefing.smsCredits ?? '—'}</h3>
          <small className={`dailyBriefingTone dailyBriefingTone--${briefing.smsCreditsLevel}`}>{briefing.smsCreditsLevel === 'critical' ? 'Critical' : briefing.smsCreditsLevel === 'warning' ? 'Low' : briefing.smsCreditsLevel === 'healthy' ? 'Healthy' : 'Unavailable'}</small>
        </div>
      </div>

      <div className="dailyBriefingSection">
        <div className="dailyBriefingSectionHeader">
          <div>
            <p className="dailyBriefingEyebrow">Priority queue</p>
            <h4>What deserves attention first</h4>
          </div>
        </div>
        <div className="dailyBriefingPriorityGrid">
          {priorities.map((item) => (
            <div key={item.title} className="dailyBriefingPriorityCard">
              <div className="dailyBriefingPriorityTop">
                <span className="dailyBriefingPriorityIcon">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                </span>
                <h5>{item.title}</h5>
              </div>
              <p>{item.detail}</p>
              <button type="button" className="dailyBriefingActionBtn" onClick={item.action}>
                {item.actionLabel}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="dailyBriefingLayout">
        <div className="dailyBriefingPanel">
          <div className="dailyBriefingSectionHeader">
            <div>
              <p className="dailyBriefingEyebrow">Today&apos;s pipeline</p>
              <h4>Applications submitted today</h4>
            </div>
            <button type="button" className="dailyBriefingLinkBtn" onClick={() => navigate('/applications')}>
              Open applications
            </button>
          </div>
          {briefing.applicationsToday.length === 0 ? (
            <div className="dailyBriefingEmptyState">No new applications have been submitted today yet.</div>
          ) : (
            <div className="dailyBriefingList">
              {briefing.applicationsToday.slice(0, 6).map((item) => (
                <button key={item.id} type="button" className="dailyBriefingListItem" onClick={() => navigate('/view-student', { state: { myData: item.userId } })}>
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.course}</span>
                  </div>
                  <small>{formatDateTime(item.appDate)}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="dailyBriefingPanel">
          <div className="dailyBriefingSectionHeader">
            <div>
              <p className="dailyBriefingEyebrow">Today&apos;s schedule</p>
              <h4>Interviews on the calendar</h4>
            </div>
            <button type="button" className="dailyBriefingLinkBtn" onClick={() => navigate('/interviews')}>
              Open interviews
            </button>
          </div>
          {briefing.interviewsToday.length === 0 ? (
            <div className="dailyBriefingEmptyState">No interviews are scheduled for today.</div>
          ) : (
            <div className="dailyBriefingList">
              {briefing.interviewsToday.slice(0, 6).map((item) => (
                <button key={item.id} type="button" className="dailyBriefingListItem" onClick={() => navigate('/interviews')}>
                  <div>
                    <strong>{item.studentName}</strong>
                    <span>{item.course} • {item.status}</span>
                  </div>
                  <small>{formatDateTime(item.scheduledFor)}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="dailyBriefingPanel">
          <div className="dailyBriefingSectionHeader">
            <div>
              <p className="dailyBriefingEyebrow">Collected today</p>
              <h4>Payments recorded today</h4>
            </div>
            <button type="button" className="dailyBriefingLinkBtn" onClick={() => navigate('/income')}>
              Open income
            </button>
          </div>
          {briefing.paymentsToday.length === 0 ? (
            <div className="dailyBriefingEmptyState">No payments have been recorded today yet.</div>
          ) : (
            <div className="dailyBriefingList">
              {briefing.paymentsToday.slice(0, 6).map((item) => (
                <button key={`${item.type}-${item.id}`} type="button" className="dailyBriefingListItem" onClick={() => navigate(item.type === 'Admission fee' ? '/admission-fees' : '/income')}>
                  <div>
                    <strong>{item.studentName}</strong>
                    <span>{item.type} • {item.paymentMethod}</span>
                  </div>
                  <small>{formatCurrency(item.displayAmount, item.currency)}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="dailyBriefingPanel">
          <div className="dailyBriefingSectionHeader">
            <div>
              <p className="dailyBriefingEyebrow">Follow-up watch</p>
              <h4>Students needing fee attention</h4>
            </div>
            <button type="button" className="dailyBriefingLinkBtn" onClick={() => navigate('/fee-reminders')}>
              Open fee reminders
            </button>
          </div>
          {briefing.overdueStudents.length === 0 ? (
            <div className="dailyBriefingEmptyState">No overdue monthly fee follow-ups are standing out right now.</div>
          ) : (
            <div className="dailyBriefingList">
              {briefing.overdueStudents.slice(0, 6).map((item) => (
                <button key={item.id} type="button" className="dailyBriefingListItem" onClick={() => navigate('/view-student', { state: { myData: item.userId } })}>
                  <div>
                    <strong>{item.name}</strong>
                    <span>{item.course}</span>
                  </div>
                  <small>{formatCurrency(item.amountDue, 'KES')} due</small>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="dailyBriefingPanel dailyBriefingPanel--wide">
          <div className="dailyBriefingSectionHeader">
            <div>
              <p className="dailyBriefingEyebrow">Communications watch</p>
              <h4>Media consent requests still waiting</h4>
            </div>
            <button type="button" className="dailyBriefingLinkBtn" onClick={() => navigate('/media-consent')}>
              Open media consent
            </button>
          </div>
          {briefing.pendingMediaRequests.length === 0 ? (
            <div className="dailyBriefingEmptyState">No pending media consent requests need attention right now.</div>
          ) : (
            <div className="dailyBriefingList">
              {briefing.pendingMediaRequests.map((item) => (
                <button key={item.id} type="button" className="dailyBriefingListItem" onClick={() => navigate('/media-consent')}>
                  <div>
                    <strong>{item.title || item.help || 'Media consent request'}</strong>
                    <span>{item.pending || 0} pending • {item.approved || 0} approved • {item.declined || 0} declined</span>
                  </div>
                  <small>{formatDateTime(item.createdAt)}</small>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DailyBriefingComp;
